import { CheckIcon, ErrorIcon } from '@assets/icons'
import { Box, Typography, useTheme } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
	value?: string
	onValidation: (val: boolean) => void
	email: string
	className?: string
	collapsed?: boolean
}

const lowerCaseRegex = /.*[a-z].*/
const upperCaseRegex = /.*[A-Z].*/
const numberRegex = /\d+/

const PasswordStrength = ({ value, onValidation, email, className }: Props) => {
	const { palette } = useTheme()
	const { t } = useTranslation('translation')

	useEffect(() => {
		let isValid = true

		if (
			value &&
			(!lowerCaseRegex.test(value) ||
				!upperCaseRegex.test(value) ||
				!numberRegex.test(value) ||
				value.length < 8 ||
				value === email)
		) {
			isValid = false
		}

		onValidation(isValid)
	}, [value, onValidation, email])

	const renderRule = (text: string, condition: () => boolean) => (
		<Box display="flex" alignItems="center">
			{condition() ? <ErrorIcon /> : <CheckIcon />}
			<Typography
				variant="subtitle2"
				color={
					condition()
						? `${palette.statuses.declined}`
						: `${palette.fonts.secondary}`
				}
				ml={1}
			>
				{text}
			</Typography>
		</Box>
	)

	return (
		<Box className={className} marginX={1}>
			<Typography color={`${palette.fonts.secondary}`} variant="subtitle2">
				{t('translation:passwordRuleHeader')}
			</Typography>
			<Box marginX={0.5}>
				{renderRule(t('translation:passwordRuleMinSymbols'), () =>
					value?.length ? value.length < 8 : false
				)}
				{renderRule(
					t('translation:passwordRuleUppercase'),
					() => !(value && upperCaseRegex.test(value))
				)}
				{renderRule(
					t('translation:passwordRuleLowercase'),
					() => !(value && lowerCaseRegex.test(value))
				)}
				{renderRule(
					t('translation:passwordRuleNumber'),
					() => !(value && numberRegex.test(value))
				)}
				{renderRule(
					t('translation:passwordRuleSameAsEmail'),
					() => value === email
				)}
			</Box>
		</Box>
	)
}

export { PasswordStrength }
